import { useMemo } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import { Form } from 'antd';
import { DarwinSelect } from 'components/darwin-ui';
import { useTranslation } from 'react-i18next';
import styles from './index.module.css';
import { EUDR_HS_CODES, EUDR_PARTIAL_PREFIX } from 'constants/eudr.constants';
import { CreateProductWithoutFormData } from 'types/productsTypes';
import { EudrHsCodeItem, EudrHsCodesMap } from 'types/eudrTypes';
import { EudrSectionShell } from 'components/ui/EudrSectionShell';

const EUDR_FIELDS = {
  isEudrEnabled: 'isEudrEnabled' as const,
  hsCode: 'hsCode' as const,
} as const;

const { Option } = DarwinSelect;

interface EUDRSectionProps {
  control: Control<CreateProductWithoutFormData>;
  errors: FieldErrors<CreateProductWithoutFormData>;
  setValue: UseFormSetValue<CreateProductWithoutFormData>;
  isViewForm: boolean;
}

type HsOption = {
  id: string;
  label: string;
  disabled?: boolean;
  className?: string;
};

export const EUDRSection = ({
  control,
  errors,
  setValue,
  isViewForm,
}: EUDRSectionProps) => {
  const { t } = useTranslation();

  const isEudrEnabled = useWatch({
    control,
    name: EUDR_FIELDS.isEudrEnabled,
  });

  const hsCodeOptions = useMemo<HsOption[]>(() => {
    return Object.entries(EUDR_HS_CODES as EudrHsCodesMap).flatMap(
      ([category, items]) => {
        const header: HsOption = {
          id: `__header__${category}`,
          label: t(`eudr.headers.${category}`),
          disabled: true,
          className: styles.dropdownGroupLabel,
        };

        const children: HsOption[] = items.map((item: EudrHsCodeItem) => ({
          id: item.isPartial ? `${EUDR_PARTIAL_PREFIX}${item.code}` : item.code,
          label: `${item.isPartial ? EUDR_PARTIAL_PREFIX : ''}${
            item.code
          } - ${t(item.translationKey)}`,
        }));

        return [header, ...children];
      }
    );
  }, [t]);

  const hsCodeError = errors.hsCode;

  return (
    <Controller
      name={EUDR_FIELDS.isEudrEnabled}
      control={control}
      render={({ field }) => (
        <EudrSectionShell
          title={t('product.new-product-form.eudr-title')}
          subtitle={t('product.new-product-form.eudr-subtitle')}
          checked={!!field.value}
          onCheckChange={isChecked => {
            field.onChange(isChecked);
            if (!isChecked) {
              setValue(EUDR_FIELDS.hsCode, '');
            }
          }}
          checkboxLabel={t('product.new-product-form.eudr-regulation')}
          checkboxTooltip={t('product.new-product-form.eudr-checkbox-tooltip')}
          checkboxDisabled={isViewForm}
        >
          <Form.Item
            label={t('product.new-product-form.hs-code')}
            validateStatus={hsCodeError ? 'error' : undefined}
            help={
              hsCodeError?.message ? String(hsCodeError.message) : undefined
            }
            className={styles.formInputTitle}
            required={!!field.value}
          >
            <Controller
              name={EUDR_FIELDS.hsCode}
              control={control}
              rules={{
                validate: value => {
                  if (!field.value) {
                    return true;
                  }

                  if (!value || value.startsWith('__header__')) {
                    return t('product.new-product-form.hs-code-required');
                  }

                  return true;
                },
              }}
              render={({ field: hsField }) => (
                <DarwinSelect
                  {...hsField}
                  disabled={isViewForm}
                  optionFilterProp="label"
                  showSearch
                  allowClear
                  filterOption={(input, option) => {
                    if (!option || option.disabled) return false;
                    return String(option.label)
                      .toLowerCase()
                      .includes(input.toLowerCase());
                  }}
                >
                  {hsCodeOptions.map(item => (
                    <Option
                      key={item.id}
                      value={item.id}
                      label={item.label}
                      disabled={item.disabled}
                      className={item.className}
                    >
                      {item.label}
                    </Option>
                  ))}
                </DarwinSelect>
              )}
            />
          </Form.Item>
        </EudrSectionShell>
      )}
    />
  );
};
